import { apiRead } from '../admin/primitives.js'

/**
 * SUPERVISION — ce que le navigateur sait de Sonde (21/10/2026).
 *
 * Le navigateur ne parle JAMAIS à Sonde : il lit le relais de Gestion
 * (`/api/v1/supervision/*`), qui appelle Sonde côté serveur avec ses jetons.
 * Ce module porte les formes renvoyées par ce relais et UNE façon de les lire.
 *
 * Pourquoi valider la forme : `apiGet` résout aussi sur une erreur HTTP. Une
 * réponse qui n'a pas la forme attendue n'est jamais affichée comme une
 * donnée — elle devient un état (« non raccordée », « indisponible »…).
 *
 * Règle d'or : Sonde absente ou injoignable = GRIS (« Supervision
 * indisponible »), jamais rouge, jamais « hors ligne ». Une valeur absente
 * s'écrit « — », jamais 0.
 */

/* ═══════════════ Formes de Sonde relayées telles quelles (contrat v1) ═══════════════ */

export type SondeStatutSource = 'OK' | 'DEGRADED' | 'UNAVAILABLE' | 'DISABLED' | 'NOT_CONFIGURED'

export interface SondeChampCle { name: string; label: string; secret: boolean; required: boolean; type: string }

/** Métadonnées d'une clé : JAMAIS la clé elle-même (CredentialMeta). */
export interface SondeCredentialMeta {
  configured: boolean
  required: boolean
  kind: string | null
  fields: SondeChampCle[]
  /** Âge en jours de Paris (minuits franchis). */
  age_days: number | null
  set_at: string | null
  set_by: string | null
  last_test_at: string | null
  last_test_ok: boolean | null
  last_test_detail: string | null
}

export interface SondeTlsInfo {
  mode: 'pin' | 'ca' | 'none'
  fingerprint: string | null
  pinned_at: string | null
  confirmed_by: string | null
  confirmed_at: string | null
  /** Empreinte vue lors d'un CERT_CHANGED, en attente de confiance. */
  pending_fingerprint: string | null
}

export interface SondeSourceView {
  code: string
  label: string
  family: string
  kind: string
  description: string
  gestion_integration_key: string | null
  status: SondeStatutSource
  status_since: string | null
  last_success_at: string | null
  last_error_code: string | null
  last_error_at: string | null
  consecutive_failures: number
  credential: SondeCredentialMeta
  tls: SondeTlsInfo
}

export interface SondeTestResult {
  ok: boolean
  code: string | null
  message_fr: string
  latency_ms: number | null
  observed_fingerprint: string | null
  details?: Record<string, unknown>
}

export interface SondeCredentialPutResult { stored: boolean; test: SondeTestResult; credential: SondeCredentialMeta }

/* ═══════════════ Formes propres au relais de Gestion ═══════════════ */

export type JetonSonde = 'api' | 'worker'

export interface ConnexionSonde {
  configured: boolean
  api_url: string | null
  tokens: Record<JetonSonde, { set: boolean; updated_at: string | null }>
  status: {
    reachable: boolean
    error_code: string | null
    message: string | null
    version: string | null
    shadow_mode: boolean | null
    shadow_release: string | null
    shadow_summary_fr: string | null
  }
  contract: { local_sha256: string; remote_sha256: string | null; aligned: boolean | null }
}

export interface ReglagesGenerauxSonde {
  etag: string
  shadow_mode: boolean
  shadow_release: string
  shadow_release_options: { value: string; label: string }[]
}

export type ProprietaireSonde = 'gestion' | 'sonde'

export interface LigneProprietaire {
  source: string
  label: string
  owner: ProprietaireSonde
  changed_at: string | null
  changed_by_name: string | null
  reason: string | null
}

export interface ProprietairesSonde { owners: LigneProprietaire[]; warnings: string[] }

export interface EtatSmtpSonde { last_attempt_at: string | null; ok: boolean | null; message: string | null }

/** Secours e-mail de Sonde (groupe `fallback`) : sans destinataire, il ne peut rien envoyer. */
export interface SecoursSonde { etag: string; enabled: boolean | null; recipients: string[] }

export interface EtatSupervision {
  state: 'ok' | 'indisponible' | 'non_configuree'
  since: string | null
  shadow_mode: boolean | null
  shadow_release: string | null
  shadow_summary_fr: string | null
}

export interface ResultatTestJeton {
  ok: boolean
  message: string
  token_name: string | null
  scopes: string[] | null
  kind_matches: boolean | null
}

/* ═══════════════ Gardes de forme ═══════════════ */

const objet = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x)
const texteOuNul = (x: unknown) => x === null || typeof x === 'string'
const boolOuNul = (x: unknown) => x === null || typeof x === 'boolean'

export function estConnexion(x: unknown): x is ConnexionSonde {
  if (!objet(x) || typeof x['configured'] !== 'boolean' || !texteOuNul(x['api_url'])) return false
  const t = x['tokens'], s = x['status'], c = x['contract']
  if (!objet(t) || !objet(t['api']) || !objet(t['worker'])) return false
  if (typeof t['api']['set'] !== 'boolean' || typeof t['worker']['set'] !== 'boolean') return false
  if (!objet(s) || typeof s['reachable'] !== 'boolean' || !boolOuNul(s['shadow_mode'])) return false
  return objet(c) && boolOuNul(c['aligned'])
}

export function estReglagesGeneraux(x: unknown): x is ReglagesGenerauxSonde {
  return objet(x) && typeof x['etag'] === 'string' && typeof x['shadow_mode'] === 'boolean'
    && typeof x['shadow_release'] === 'string' && Array.isArray(x['shadow_release_options'])
}

export function estProprietaires(x: unknown): x is ProprietairesSonde {
  return objet(x) && Array.isArray(x['owners']) && Array.isArray(x['warnings'])
    && (x['owners'] as unknown[]).every((o) => objet(o) && typeof o['source'] === 'string' && (o['owner'] === 'gestion' || o['owner'] === 'sonde'))
}

export function estSecours(x: unknown): x is SecoursSonde {
  return objet(x) && typeof x['etag'] === 'string' && boolOuNul(x['enabled'])
    && Array.isArray(x['recipients']) && (x['recipients'] as unknown[]).every((r) => typeof r === 'string')
}

export function estEtatSmtp(x: unknown): x is EtatSmtpSonde {
  return objet(x) && texteOuNul(x['last_attempt_at']) && boolOuNul(x['ok']) && texteOuNul(x['message'])
}

export function estEtatSupervision(x: unknown): x is EtatSupervision {
  return objet(x) && (x['state'] === 'ok' || x['state'] === 'indisponible' || x['state'] === 'non_configuree')
}

export function estSourceView(x: unknown): x is SondeSourceView {
  return objet(x) && typeof x['code'] === 'string' && typeof x['status'] === 'string'
    && objet(x['credential']) && Array.isArray(x['credential']['fields']) && objet(x['tls'])
}

export function estListeSources(x: unknown): x is { sources: SondeSourceView[] } {
  return objet(x) && Array.isArray(x['sources']) && (x['sources'] as unknown[]).every(estSourceView)
}

export function estResultatTest(x: unknown): x is SondeTestResult {
  return objet(x) && typeof x['ok'] === 'boolean' && typeof x['message_fr'] === 'string'
}

/* ═══════════════ Lire, et dire pourquoi on ne peut pas ═══════════════ */

/**
 * `non_raccordee` : Gestion n'a pas encore l'adresse ou les jetons de Sonde.
 * `indisponible`  : Sonde ne répond pas, ou refuse le jeton de Gestion — gris.
 * `refus`         : le compte de la personne n'a pas le droit de voir cela.
 * `erreur`        : tout le reste (réponse inattendue, Gestion muet).
 */
export type EtatLecture = 'non_raccordee' | 'indisponible' | 'refus' | 'erreur'

export type Lecture<T> =
  | { etat: 'ok'; data: T }
  | { etat: EtatLecture; message: string; code: string | null }

const MESSAGES: Record<EtatLecture, string> = {
  non_raccordee: 'Sonde n’est pas encore raccordée.',
  indisponible: 'Supervision indisponible : Sonde ne répond pas pour l’instant.',
  refus: 'Votre compte ne permet pas de voir cette partie de Sonde.',
  erreur: 'Lecture impossible pour l’instant.',
}

/** Range un échec (statut + corps d'erreur de Gestion) dans un des quatre états. */
export function classerEchec(status: number, data: unknown): { etat: EtatLecture; message: string; code: string | null } {
  const code = objet(data) && typeof data['error'] === 'string' ? data['error'] : null
  const message = objet(data) && typeof data['message'] === 'string' && data['message'] ? data['message'] : null
  if (code === 'SONDE_NOT_CONFIGURED') return { etat: 'non_raccordee', message: MESSAGES.non_raccordee, code }
  if (code === 'SONDE_UNAVAILABLE' || code === 'SONDE_TOKEN_REFUSED' || status === 502 || status === 503 || status === 504) {
    // Toujours dit « Supervision indisponible » d'abord : jamais « hors ligne », jamais une panne.
    const detail = message && !/^supervision indisponible/i.test(message) ? `Supervision indisponible — ${message}` : message
    return { etat: 'indisponible', message: detail ?? MESSAGES.indisponible, code }
  }
  if (status === 401 || status === 403) return { etat: 'refus', message: MESSAGES.refus, code }
  return { etat: 'erreur', message: message ?? MESSAGES.erreur, code }
}

export async function lireSupervision<T>(chemin: string, valide: (x: unknown) => x is T): Promise<Lecture<T>> {
  try {
    const r = await apiRead<unknown>(`/api/v1/supervision${chemin}`)
    if (r.status >= 200 && r.status < 300 && valide(r.data)) return { etat: 'ok', data: r.data }
    if (r.status >= 200 && r.status < 300) return { etat: 'erreur', message: 'Réponse inattendue de Gestion : rien n’est affiché plutôt qu’un chiffre faux.', code: null }
    return classerEchec(r.status, r.data)
  } catch {
    return { etat: 'erreur', message: 'Gestion n’a pas répondu.', code: null }
  }
}

/** Message d'une mutation refusée, en clair. Le step-up annulé n'est pas une panne. */
export function messageEchecMutation(status: number, data: unknown, rien = 'rien n’a été modifié'): string {
  const code = objet(data) && typeof data['error'] === 'string' ? data['error'] : null
  if (status === 403 && code === 'STEPUP_REQUIRED') return `Confirmation d’identité annulée — ${rien}.`
  const c = classerEchec(status, data)
  if (c.etat === 'non_raccordee') return `Sonde n’est pas encore raccordée — ${rien}.`
  if (c.etat === 'refus' && !(objet(data) && typeof data['message'] === 'string')) return `Vos droits ne permettent pas ce geste — ${rien}.`
  return c.message
}

/* ═══════════════ Affichage ═══════════════ */

export const fmtDateHeure = (iso: string | null) => iso ? new Date(iso).toLocaleString('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
}) : '—'

export const fmtHeure = (iso: string | null) => iso ? new Date(iso).toLocaleTimeString('fr-FR', {
  hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
}) : '—'

/** Statut d'une source de Sonde en clair, et sa pastille (gris si indisponible). */
export const STATUT_SOURCE: Record<SondeStatutSource, { texte: string; pill: string }> = {
  OK: { texte: 'Lecture réussie', pill: 'lc-pill--ok' },
  DEGRADED: { texte: 'Dégradée', pill: 'lc-pill--warning' },
  UNAVAILABLE: { texte: 'Indisponible', pill: 'lc-pill--idle' },
  NOT_CONFIGURED: { texte: 'Non configurée', pill: 'lc-pill--idle' },
  DISABLED: { texte: 'Désactivée', pill: 'lc-pill--idle' },
}

/**
 * Sonde publie-t-elle les alertes des sources basculées ? Condition pour
 * confier une source (le serveur la vérifie aussi) : sinon plus personne ne
 * prévient pour elle. null = illisible.
 */
export function publieSourcesBasculees(shadowMode: boolean | null, release: string | null): boolean | null {
  if (shadowMode === null) return null
  return !shadowMode || release === 'switched' || release === 'switched_and_new'
}

/**
 * Ce que Sonde publie, conséquence d'abord. `shadow_release` vient de Sonde
 * (liste fermée) ; une valeur inconnue garde le libellé que Sonde lui donne.
 * `confiees` : nombre de sources déjà confiées à Sonde (Gestion se tait pour elles).
 */
export function consequencePublication(shadowMode: boolean | null, release: string | null, libelle?: string, confiees = 0): string {
  if (shadowMode === null) return '—'
  if (!shadowMode) {
    return confiees < 9
      ? 'Sonde prévient pour tout ce qu’elle surveille : le mode ombre est coupé. Doublons possibles avec Gestion tant que toutes les sources ne lui sont pas confiées.'
      : 'Sonde prévient pour tout ce qu’elle surveille : le mode ombre est coupé.'
  }
  if (release === 'none') {
    return confiees > 0
      ? `Rien : Sonde observe sans alerter. Attention : pour ${confiees > 1 ? `les ${confiees} sources confiées` : 'la source confiée'} à Sonde, PERSONNE ne prévient.`
      : 'Rien : Gestion seul prévient. Sonde observe sans alerter.'
  }
  if (release === 'switched') return 'Sources basculées : Sonde prévient pour les sources qui lui sont confiées, Gestion pour les autres.'
  if (release === 'switched_and_new') return 'Basculées + tout ce que Gestion ne surveillait pas : Sonde prévient pour les sources confiées et pour tout ce qu’elle est seule à voir.'
  return libelle ?? release ?? '—'
}
